import type { Puzzle } from "./types";

/**
 * The printed board, described once in millimetres.
 *
 * Two renderers draw it — the React page and the build-time static HTML — and they
 * have to agree exactly, so neither owns the geometry. Millimetres rather than rem or
 * vw because this ends up on paper: a cell is a box someone puts a pencil mark in, and
 * "about 13mm, depending on the root font size" is not a specification.
 *
 * It is drawn as one SVG rather than a div per cell. A filled square used to be a CSS
 * background, and browsers do not print backgrounds unless the person printing goes
 * and ticks "Background graphics" — so the answer key came out of the printer as an
 * empty grid. SVG shapes are foreground content and print by default.
 */

/**
 * The three measurements that decide whether a board fits the paper, per grid size:
 * the cell, the clue gutter down the left, and the clue band across the top. They live
 * here rather than in CSS because the sheet layout has to be able to answer "does this
 * fit on one page" in a test, and a stylesheet cannot be asked.
 */
const CELL_MM: Record<number, number> = { 5: 12, 10: 10, 15: 9 };
const GUTTER_MM: Record<number, number> = { 5: 16, 10: 24, 15: 27 };
const BAND_MM: Record<number, number> = { 5: 14, 10: 18, 15: 22 };
const DEFAULT_CELL_MM = 9;

/** Every fifth line is heavy, exactly as on screen: it is what makes counting to
    eleven along a 15-wide row possible without a finger on the paper. */
export const GRID_LINE_MM = 0.2;
export const GRID_LINE_HEAVY_MM = 0.5;
const GUIDE_EVERY = 5;

export type PrintLine = { x1: number; y1: number; x2: number; y2: number; heavy: boolean };
export type PrintFill = { x: number; y: number };

export type PrintBoardGeometry = {
  /** Cell edge in mm. */
  cell: number;
  /** Row-clue column width and column-clue band height, in mm. */
  gutter: number;
  band: number;
  /** How far left to pull the board so the grid — not the board box, which carries a
      clue gutter on one side only — sits centred on the sheet. Clamped so the gutter
      never crosses the page margin, which at 15×15 it otherwise would. */
  offset: number;
  /** Board edge in mm, without the stroke overhang. */
  span: number;
  /** viewBox and intrinsic size, padded by half a heavy stroke so the outer rules are
      not clipped in half by the edge of the SVG. */
  viewBox: string;
  widthMm: number;
  fills: PrintFill[];
  lines: PrintLine[];
};

export function printCellMm(size: number): number {
  return CELL_MM[size] ?? DEFAULT_CELL_MM;
}

/** Width of the row-clue column, wide enough for the longest clue list at that size. */
export function printGutterMm(size: number): number {
  return GUTTER_MM[size] ?? 24;
}

/** Height of the column-clue band above the grid. */
export function printBandMm(size: number): number {
  return BAND_MM[size] ?? 18;
}

/** `cellMm` overrides the printed size — the answer key draws the same boards small
    enough that a page of them fits on one sheet. */
/**
 * A board is a clue gutter plus a grid, so centring the board leaves the grid sitting
 * half a gutter to the right of the page's centre line. This is how far left to pull
 * it — at most half a gutter, and never so far that the gutter runs off the margin.
 */
export function printCentringOffsetMm(gutter: number, span: number, safeWidth = 182): number {
  const boardLeft = Math.max(0, (safeWidth - gutter - span) / 2);
  return Math.min(gutter / 2, boardLeft);
}

export function printBoardGeometry(puzzle: Puzzle, answer: boolean, cellMm?: number): PrintBoardGeometry {
  const size = puzzle.size;
  const cell = cellMm ?? printCellMm(size);
  const span = cell * size;
  const overhang = GRID_LINE_HEAVY_MM / 2;

  const fills: PrintFill[] = [];
  if (answer) {
    for (let index = 0; index < puzzle.solution.length; index += 1) {
      if (puzzle.solution[index] === 1) {
        fills.push({ x: (index % size) * cell, y: Math.floor(index / size) * cell });
      }
    }
  }

  const lines: PrintLine[] = [];
  for (let step = 0; step <= size; step += 1) {
    const at = step * cell;
    const heavy = step % GUIDE_EVERY === 0;
    lines.push({ x1: at, y1: 0, x2: at, y2: span, heavy });
    lines.push({ x1: 0, y1: at, x2: span, y2: at, heavy });
  }

  const gutter = cellMm ? 0 : printGutterMm(size);

  return {
    cell,
    gutter,
    band: cellMm ? 0 : printBandMm(size),
    offset: printCentringOffsetMm(gutter, span),
    span,
    viewBox: `${-overhang} ${-overhang} ${span + overhang * 2} ${span + overhang * 2}`,
    widthMm: span + overhang * 2,
    fills,
    lines,
  };
}
